/**
 * Integrytas Group - Common UI
 *
 * Expand/contract "related children" pattern for list screens.
 *
 * A list row that is the PARENT of other records (a role has users, a job has
 * roles/approvers, a page has roles/sub-pages, ...) gets a plus/minus disclosure
 * in a leading column. Expanding it reveals a full-width detail panel that shows
 * each non-empty child collection as a SIDE-BY-SIDE card.
 *
 * The presentation is generic: a page builds a `RelatedChildGroup[]` per row
 * (fetching its own children through the gateway) and hands it to
 * <RelatedChildrenPanel>. The disclosure button and the row-expansion state hook
 * are provided so every list page wires the pattern identically.
 *
 * Usage in a PersonalizedGrid table:
 *   const detail = useExpandedRows<number>();
 *   // thead: add one leading <th className="w-7 ..."> before the first column
 *   // row:   add a leading <td> with <RelatedExpandButton ...>
 *   // after each row, when detail.isOpen(id), render a full-width <tr> whose
 *   //   single <td colSpan={visibleCols.length + N}> hosts <RelatedChildrenPanel>.
 */
import { useState, type ReactNode } from 'react';
import type { AuthSession } from '../../types/auth';
import { ChildDetailModal, type ChildDetailTarget } from './ChildDetailModal';

export interface RelatedChildItem {
  id: string | number;
  label: string;          // resolved display name - never a raw id/code (§17.B4)
  title?: string;         // hover text (e.g. the underlying code or email)
  badges?: string[];      // compact mono badges (e.g. V/A/C/D/S perms, a sequence #)
  /**
   * Optional leading glyph rendered before the label - a locale flag, a module
   * icon. Decoration only: it never replaces the resolved `label` (§17.B4), so
   * a child stays readable when the glyph cannot render.
   */
  icon?: ReactNode;
}

export interface RelatedChildGroup {
  key: string;
  title: string;                       // card header (localised by the caller)
  items: RelatedChildItem[];
  badgeTitles?: Record<string, string>; // optional badge -> hover label map
  /**
   * Drill-down identity. With `entity` the child opens its OWN edit form from
   * the registry (editable or read-only per the CHILD page's can_change);
   * with only `table`/`pkColumn` it opens a generic read-only field view.
   * With neither, items render exactly as before — not clickable, because a
   * click that does nothing is worse than no affordance.
   */
  entity?: string;
  table?: string;
  pkColumn?: string;
}

/** Row-expansion state for the related-children disclosure (one open set). */
export function useExpandedRows<T extends string | number = number>() {
  const [expanded, setExpanded] = useState<Set<T>>(new Set());
  const toggle = (id: T) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const isOpen = (id: T) => expanded.has(id);
  const clear = () => setExpanded(new Set<T>());
  return { expanded, toggle, isOpen, clear };
}

/** Leading-column plus/minus disclosure. Renders a spacer when there are no children. */
export function RelatedExpandButton({
  open, hasChildren, onToggle, expandLabel, collapseLabel, tooltip,
}: {
  open: boolean;
  hasChildren: boolean;
  onToggle: () => void;
  expandLabel: string;
  collapseLabel: string;
  tooltip?: string;
}) {
  if (!hasChildren) return <span className="inline-block h-5 w-5" aria-hidden />;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      aria-label={open ? collapseLabel : expandLabel}
      title={tooltip ?? (open ? collapseLabel : expandLabel)}
      className={
        'inline-flex h-5 w-5 items-center justify-center rounded border text-csm-sm font-semibold leading-none ' +
        (open
          ? 'border-primary bg-primary text-white'
          : 'border-csm-border bg-white text-csm-ink-soft hover:border-primary hover:text-primary')
      }
    >{open ? '−' : '+'}</button>
  );
}

function ChildCard({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    // Best fit: the card sizes to its content between a readable floor and a
    // ceiling, and does NOT grow. `flex-1 basis-64` used to stretch a lone group
    // across the entire panel, which is unreadable — a single short child name
    // stranded on a full-width row. Several groups still sit side by side and
    // wrap, each only as wide as it needs.
    <div className="w-fit min-w-[220px] max-w-[420px] shrink-0 overflow-hidden rounded-md border border-csm-border bg-white">
      <div className="flex items-center justify-between border-b border-csm-border bg-csm-surface-alt px-2 py-1">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        <span className="rounded-full bg-primary/10 px-1.5 text-csm-2xs font-semibold text-primary">{count}</span>
      </div>
      <ul className="max-h-44 divide-y divide-csm-border-soft overflow-auto">{children}</ul>
    </div>
  );
}

/**
 * The side-by-side detail panel. Renders only non-empty groups; when nothing
 * has children it shows a muted empty hint.
 */
export function RelatedChildrenPanel({
  groups, emptyLabel, session, onSaved,
}: {
  groups: RelatedChildGroup[];
  emptyLabel?: string;
  /** Supply to make children clickable; omit and the panel stays display-only. */
  session?: AuthSession;
  /** Called after a child edit saves, so the parent can refresh its own rows. */
  onSaved?: () => void;
}) {
  const [open, setOpen] = useState<(ChildDetailTarget & { groupKey: string }) | null>(null);
  const nonEmpty = groups.filter(g => g.items.length > 0);
  if (nonEmpty.length === 0) {
    return <div className="px-1 py-2 text-csm-sm text-csm-muted">{emptyLabel ?? 'No related records.'}</div>;
  }
  return (
    <div className="flex flex-wrap gap-3">
      {nonEmpty.map(g => {
        const drillable = !!session && (!!g.entity || (!!g.table && !!g.pkColumn));
        return (
        <ChildCard key={g.key} title={g.title} count={g.items.length}>
          {g.items.map(it => (
            <li
              key={it.id}
              className={
                'flex items-center justify-between gap-2 px-2 py-1 text-csm-sm ' +
                (drillable ? 'cursor-pointer hover:bg-csm-surface-alt' : '')
              }
              onClick={drillable
                ? () => setOpen({ groupKey: g.key, entity: g.entity, table: g.table, pkColumn: g.pkColumn, pk: it.id, label: it.label })
                : undefined}
              role={drillable ? 'button' : undefined}
              tabIndex={drillable ? 0 : undefined}
              onKeyDown={drillable
                ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen({ groupKey: g.key, entity: g.entity, table: g.table, pkColumn: g.pkColumn, pk: it.id, label: it.label }); } }
                : undefined}
            >
              <span className="flex min-w-0 items-center gap-1.5 text-csm-ink" title={it.title}>
                {it.icon && <span className="shrink-0 select-none leading-none" aria-hidden>{it.icon}</span>}
                <span className="truncate">{it.label}</span>
              </span>
              {it.badges && it.badges.length > 0 && (
                <span className="flex shrink-0 gap-0.5">
                  {it.badges.map(b => (
                    <span
                      key={b}
                      title={g.badgeTitles?.[b] ?? b}
                      className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded border border-csm-border-soft bg-csm-surface-alt px-1 font-mono text-csm-2xs text-csm-ink-soft"
                    >{b}</span>
                  ))}
                </span>
              )}
            </li>
          ))}
        </ChildCard>
        );
      })}
      {open && session && (
        <ChildDetailModal
          session={session}
          target={open}
          onClose={() => setOpen(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
